/**
 * useRaelaRating
 * --------------
 * Lets a member thumb up / thumb down RAELA's reply. The rating is
 * written to `raela_training_contributions.rating` (1 = up, -1 = down)
 * for the most recent contribution row matching this trace_id.
 *
 * Visible-to-member: after rating, the UI persists "you rated this 👍"
 * locally per message id so we don't have to re-query.
 */
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Thumb = 1 | -1 | 0;

const KEY = "raela:ratings:v1";

const readMap = (): Record<string, Thumb> => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
};

const writeMap = (m: Record<string, Thumb>) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(m));
};

export function useRaelaRating() {
  const [map, setMap] = useState<Record<string, Thumb>>(readMap);

  const get = useCallback((messageId: string): Thumb => map[messageId] ?? 0, [map]);

  const rate = useCallback(
    async (messageId: string, traceId: string | null, value: Thumb) => {
      const next = { ...readMap(), [messageId]: value };
      writeMap(next);
      setMap(next);

      if (!traceId || value === 0) return { error: null };

      const { error } = await supabase
        .from("raela_training_contributions")
        .update({ rating: value })
        .eq("trace_id", traceId);
      return { error: error?.message ?? null };
    },
    [],
  );

  return { get, rate };
}
